import React from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { formatCurrency } from "../../../../constants/investmentLeague";

const PlaneIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" fill="currentColor" />
  </svg>
);

const InfoCircleIcon = ({ color = "#38BDF8" }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const TrophyIcon = ({ color = "#F59E0B" }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h2" />
    <path d="M18 9h2a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-2" />
    <path d="M6 3h12v7a6 6 0 0 1-12 0V3z" fill="rgba(245, 158, 11, 0.15)" />
    <path d="M12 16v5" />
    <path d="M8 21h8" />
  </svg>
);

const GreenCircleCheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" fill="rgba(16, 185, 129, 0.15)" />
    <polyline points="16 9 10 15 7 12" />
  </svg>
);

const AmberCircleAlertIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" fill="rgba(245, 158, 11, 0.15)" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const ReinvestSidebar = ({
  availableCredit = 0,
  allocatedAmount = 0,
  remainingCredit = 0,
  isSubmitting = false,
  onSubmit,
  windowIsOpen = true,
  windowEndDate = "31 Oct 2026",
  investorUnit = "MAC 2 - Hercules",
  jackpotEligibility,
  missingUnits = [],
  previousQuarter = "Q2",
  upcomingQuarter = "Q3",
  isFullyAllocated = false,
  isOverAllocated = false,
}) => {
  const theme = useTheme();
  const t = theme.custom;
  const isDark = theme.palette.mode === "dark";

  const overAllocated = isOverAllocated || remainingCredit < 0;
  const isZeroAllocated = allocatedAmount === 0;
  const missingCount = missingUnits.length;

  const canSubmit =
    windowIsOpen &&
    !isSubmitting &&
    !overAllocated &&
    !isZeroAllocated;

  // Determine jackpot status badge
  const statusType = missingCount > 0 ? "At Risk" : (jackpotEligibility?.status || "Eligible");

  const cardStyle = {
    backgroundColor: t.surface,
    border: `1px solid ${t.border2}`,
    borderRadius: "12px",
    p: 2.5,
    boxShadow: isDark ? "0 4px 16px rgba(0, 0, 0, 0.25)" : "0 2px 8px rgba(3, 81, 77, 0.06)",
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {/* 1. Investment Summary Card */}
      <Box sx={cardStyle}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "1.05rem",
            color: t.text,
            letterSpacing: "-0.01em",
            mb: 2,
          }}
        >
          Investment Summary
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2.5 }}>
          {/* Available Credit */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography sx={{ fontSize: "0.82rem", color: t.text2 }}>
              Available Credit ({previousQuarter})
            </Typography>
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: t.text }}>
              {formatCurrency(availableCredit)}
            </Typography>
          </Box>

          {/* Total Allocated */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography sx={{ fontSize: "0.82rem", color: t.text2 }}>
              Total Allocated for {upcomingQuarter}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.95rem",
                fontWeight: 700,
                color: isOverAllocated ? (isDark ? "#EF4444" : "#DC2626") : t.text,
              }}
            >
              {formatCurrency(allocatedAmount)}
            </Typography>
          </Box>

          {/* Remaining Credit */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography sx={{ fontSize: "0.82rem", color: t.text2 }}>
              Remaining Credit
            </Typography>
            <Typography
              sx={{
                fontSize: "0.95rem",
                fontWeight: 700,
                color: overAllocated ? (isDark ? "#EF4444" : "#DC2626") : t.text,
              }}
            >
              {overAllocated
                ? "$0"
                : isFullyAllocated
                ? "$0"
                : remainingCredit < 0
                ? `-${formatCurrency(Math.abs(remainingCredit))}`
                : formatCurrency(remainingCredit)}
            </Typography>
          </Box>
        </Box>

        {/* Submit Button */}
        <Button
          fullWidth
          variant="contained"
          disabled={!canSubmit}
          onClick={onSubmit}
          startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <PlaneIcon />}
          sx={{
            backgroundColor: isDark ? "#10B981" : "#03514D",
            color: isDark ? "#092419" : "#FFFFFF",
            fontWeight: 700,
            fontSize: "0.9rem",
            textTransform: "none",
            borderRadius: "8px",
            py: 1.2,
            mb: 2,
            "&:hover": {
              backgroundColor: isDark ? "#059669" : "#023D3A",
            },
            "&.Mui-disabled": {
              backgroundColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(3, 81, 77, 0.12)",
              color: isDark ? "rgba(255, 255, 255, 0.3)" : "rgba(3, 81, 77, 0.4)",
            },
          }}
        >
          {isSubmitting ? "Submitting Allocations..." : `Submit ${upcomingQuarter} Investments`}
        </Button>

        {/* Subtext info */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Box sx={{ mt: "2px", flexShrink: 0 }}>
            <InfoCircleIcon color={isDark ? "#38BDF8" : t.accent} />
          </Box>
          <Typography sx={{ fontSize: "0.74rem", color: t.text3, lineHeight: 1.4 }}>
            You can submit your investments multiple times before the deadline. The latest submission will be considered.
          </Typography>
        </Box>
      </Box>

      {/* 2. Jackpot Eligibility Status Card */}
      <Box sx={cardStyle}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TrophyIcon color={isDark ? "#F59E0B" : "#D97706"} />
            <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: t.text }}>
              Jackpot Eligibility Status
            </Typography>
          </Box>
          <Chip
            label={statusType}
            size="small"
            sx={{
              fontWeight: 700,
              fontSize: "0.7rem",
              height: "22px",
              backgroundColor:
                statusType === "Eligible"
                  ? (isDark ? "rgba(16, 185, 129, 0.2)" : "rgba(45, 138, 91, 0.12)")
                  : statusType === "At Risk"
                  ? (isDark ? "rgba(245, 158, 11, 0.2)" : "rgba(245, 158, 11, 0.12)")
                  : (isDark ? "rgba(239, 68, 68, 0.2)" : "rgba(239, 68, 68, 0.12)"),
              color:
                statusType === "Eligible"
                  ? (isDark ? "#10B981" : "#2D8A5B")
                  : statusType === "At Risk"
                  ? (isDark ? "#F59E0B" : "#B45309")
                  : (isDark ? "#EF4444" : "#DC2626"),
              border: `1px solid ${
                statusType === "Eligible"
                  ? (isDark ? "rgba(16, 185, 129, 0.4)" : "rgba(45, 138, 91, 0.3)")
                  : statusType === "At Risk"
                  ? (isDark ? "rgba(245, 158, 11, 0.4)" : "rgba(245, 158, 11, 0.3)")
                  : (isDark ? "rgba(239, 68, 68, 0.4)" : "rgba(239, 68, 68, 0.3)")
              }`,
            }}
          />
        </Box>

        <Typography sx={{ fontSize: "0.76rem", color: t.text2, mb: 1.5 }}>
          To remain eligible for the jackpot, you must:
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, mb: 2 }}>
          {/* Rule 1 */}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Box sx={{ mt: "1px", flexShrink: 0 }}>
              <GreenCircleCheckIcon />
            </Box>
            <Typography sx={{ fontSize: "0.76rem", color: t.text, lineHeight: 1.4 }}>
              Invest in all units you invested in {previousQuarter}
            </Typography>
          </Box>

          {/* Rule 2 */}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Box sx={{ mt: "1px", flexShrink: 0 }}>
              <GreenCircleCheckIcon />
            </Box>
            <Typography sx={{ fontSize: "0.76rem", color: t.text, lineHeight: 1.4 }}>
              Have positive returns (cumulative variance) in the current quarter for all those units
            </Typography>
          </Box>

          {/* Missing units check */}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 0.5 }}>
            <Box sx={{ mt: "1px", flexShrink: 0 }}>
              <AmberCircleAlertIcon />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "0.76rem", color: isDark ? "#FBBF24" : "#B45309", fontWeight: 600, lineHeight: 1.4 }}>
                {missingCount === 0
                  ? `You have 0 units from ${previousQuarter} not selected for ${upcomingQuarter}.`
                  : `You have ${missingCount} unit${missingCount > 1 ? "s" : ""} from ${previousQuarter} not selected for ${upcomingQuarter}.`}
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", color: t.text3, lineHeight: 1.35, mt: 0.25 }}>
                Please ensure you invest in all previously invested units.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* 3. Important Notes Card */}
      <Box sx={cardStyle}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <InfoCircleIcon color={isDark ? "#38BDF8" : t.accent} />
          <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: t.text }}>
            Important Notes
          </Typography>
        </Box>

        <Box component="ol" sx={{ pl: 2.25, m: 0, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography component="li" sx={{ fontSize: "0.75rem", color: t.text2, lineHeight: 1.4 }}>
            You cannot invest in your own MAC unit ({investorUnit}).
          </Typography>
          <Typography component="li" sx={{ fontSize: "0.75rem", color: t.text2, lineHeight: 1.4 }}>
            Total investment cannot exceed your {previousQuarter} credit.
          </Typography>
          <Typography component="li" sx={{ fontSize: "0.75rem", color: t.text2, lineHeight: 1.4 }}>
            Ensure you invest in all units you invested in {previousQuarter} to remain eligible for the jackpot.
          </Typography>
          <Typography component="li" sx={{ fontSize: "0.75rem", color: t.text2, lineHeight: 1.4 }}>
            Investments will be recorded in the system with today's date once submitted.
          </Typography>
          <Typography component="li" sx={{ fontSize: "0.75rem", color: t.text2, lineHeight: 1.4 }}>
            This page is only visible to authorized Investment League users.
          </Typography>
          <Typography component="li" sx={{ fontSize: "0.75rem", color: t.text2, lineHeight: 1.4 }}>
            Re-investment window closes on {windowEndDate}.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ReinvestSidebar;

